import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import {
  clearAllPersistedWindowState,
  clearPersistedAiChatTabs,
} from '@/OS/lib/appRecovery';
import { reportWhiteScreenOfDeath } from '@/OS/lib/wsodReporter';

type OSWhiteScreenBoundaryProps = {
  children: React.ReactNode;
};

type OSWhiteScreenBoundaryState = {
  hasError: boolean;
};

class OSWhiteScreenBoundary extends React.Component<
  OSWhiteScreenBoundaryProps,
  OSWhiteScreenBoundaryState
> {
  state: OSWhiteScreenBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): OSWhiteScreenBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    clearPersistedAiChatTabs();
    clearAllPersistedWindowState();

    void reportWhiteScreenOfDeath({
      scope: 'os-root',
      appId: 'mapleos-desktop',
      appName: 'MapleOS Desktop',
      errorMessage: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
      recoveryAction: 'clear_all_window_restore_state',
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 flex items-center justify-center bg-[#0a3ea8] p-8 text-white">
          <div className="max-w-2xl space-y-6">
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-2xl font-semibold">
                <AlertTriangle className="h-8 w-8" />
                MapleOS detected a White Screen of Death
              </div>
              <p className="text-sm text-white/90">
                We captured this crash, sent a diagnostic report, and cleared the saved window state so the same broken app session does not reopen automatically.
              </p>
            </div>
            <div className="rounded-lg border border-white/20 bg-white/10 p-4 text-sm text-white/90">
              You can reload safely now. If the same issue happens again, the incident will appear in your client logs with WSOD metadata.
            </div>
            <Button
              onClick={this.handleReload}
              className="bg-white text-[#0a3ea8] hover:bg-white/90"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reload MapleOS
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default OSWhiteScreenBoundary;
